 <template>
    <div id="order-tracker" class="has-text-centered" :class="orderTrackerStyle()">
		<p class="order-promise-time" :class="orderPromiseTimeStyle()" v-if="displayPromiseTime()">
			{{order.serviceType === EServiceType.SERVICE_TYPE_TAKEOUT ? 'Pickup' : 'Delivery' }} Time: 
                {{  displayPromiseTime() }}
                <navigation-route v-if="isStreamLined"
                                  :isVueRoute="!isInjectable" 
                                  :class="{'store-navigation-color': isStreamLined}" 
                                  :link="`/order/order-confirmation?order_id=${order.id}&lb=${order.locationBase}`">
                                    view details
                                  </navigation-route>
        </p>
        <div role="group" aria-label="tracking status"
            :class="{'is-centered': $mq !== 'mobile','flex-order-1': isStreamLined }"
            class="columns is-mobile is-centered">
                <template v-for="(ICON_NAME, key, index) in ICON_NAMES.PIZZA_TRACKER">
                    <div :key="index" 
                        class="order-status-icon column is-narrow"
                        v-if="iconMap[ICON_NAME]"
						role="presentation"
                        >
                        <BaseIcon
                            :editStroke="false"
                            :isCustom="true"
                            :icon="ICON_NAME"
                            :size="($mq === 'mobile') ? '2x' : '2-5x'"
                            :classNames="trackerIconClass(ICON_NAME)"
							:ariaLabel="getAriaLabel(ICON_NAME)"
                        >
                        <template v-if="!isStreamLined">
                            {{iconMap[ICON_NAME].title}}
                            <hr class="order-hr" :class="{'active': getActiveStatus(ICON_NAME)}">
                        </template>
                        </BaseIcon>
                    </div>
                </template>
        </div>
    </div>
</template>


<script>
import * as moment from "moment";
import * as storeIcon from "@/assets/store-notification-icon.png";

import { ACCOUNT_CHECKOUT, LOCATION, RECENT_ORDERS } from "@/stores/defs";
import { EServiceType } from "@/models/interfaces/Orders/EServiceType";
import { ICON_NAMES } from "@/components/common/BaseIcon.vue";
import { mapGetters, mapActions } from "vuex";
import { SUCCESS_MSG } from '@/common/messages/success.msg';

import DateManipulator from '@/utils/DateManipulator';
import EOrderStatus from "@/models/interfaces/Orders/EOrderStatus";
import NavigationRoute from "@/components/header/NavigationRoute";
import recentOrderService from "@/services/recent.orders.service";
import WebSocketClass from "@/utils/WebSocketClass";

import notificationMixin from "@Mixin/notificationMixin";
import timeMixin from '@Mixin/timeMixin';
import trackerMixin from '@Mixin/trackerMixin';

export default {
	mixins: [
		notificationMixin,
		timeMixin,
		trackerMixin,
	],
    components: {
        "navigation-route": NavigationRoute
    },
    props: {
        order: {
            type: Object
        },
        orderLocation: {
            type: Object
        },
        isStreamLined: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        iconMap() {
            let iconMap = {
                [ICON_NAMES.PIZZA_TRACKER.ORDER_PLACED]: {
                    title: "Placed",
                    status: EOrderStatus.STATUS_NONE
                },
                [ICON_NAMES.PIZZA_TRACKER.MAKING]: {
                    title: "Making",
                    status: EOrderStatus.STATUS_CLAIMED
                },
                [ICON_NAMES.PIZZA_TRACKER.BAKING]: {
                    title: "Baking",
                    status: EOrderStatus.STATUS_PREPARED
                },
            };

            if (this.order) {
                if (this.order.serviceType === EServiceType.SERVICE_TYPE_TAKEOUT || this.order.serviceType === EServiceType.SERVICE_TYPE_PICKUP) {
                    iconMap[ICON_NAMES.PIZZA_TRACKER.PICK_UP] = {
                        title: "Ready at Store",
                        status: EOrderStatus.STATUS_STAGED
                    };
                } else if (this.order.serviceType === EServiceType.SERVICE_TYPE_DELIVERY) {
                        iconMap[ICON_NAMES.PIZZA_TRACKER.ON_ITS_WAY] = {
                        title: "On its way",
                        status: EOrderStatus.STATUS_ON_ROUTE
                    };
                }
            }
             iconMap[ICON_NAMES.PIZZA_TRACKER.EATING] = {
                title: "Eating",
                status: EOrderStatus.STATUS_DELIVERED
            }
            return iconMap;
        },
        isInjectable() {
            return this.$route.name === "injectable-header";
        },
        ...mapGetters({
            activeOrder: RECENT_ORDERS.GET_ACTIVE_ORDER
        })
    },
    data() {
        return {
            ICON_NAMES: ICON_NAMES,
            // will be current status
            status: 0,
            ordersFetched: false,
            EServiceType: EServiceType,
			EOrderStatus: EOrderStatus,
        };
    },
    methods: {
		...mapActions({
			setActiveOrder: RECENT_ORDERS.SET_ACTIVE_ORDER
		}),
        displayPromiseTime() {
			if (!this.order) {
				return false;
			}
            const promiseDate = moment(this.order.promiseDate);

            let firstDateFormat = (DateManipulator.isMomentToday(moment(promiseDate))) ? "hh:mma" : "MM/D/YYYY hh:mma";

            return promiseDate.format(firstDateFormat) + "-" + moment(promiseDate).add(this.getTimeWindow(this.order.serviceType), "minutes").format("hh:mma");
        },
        trackerIconClass(iconName) {
            const active = this.getActiveStatus(iconName) ? 'store-navigation-color': '';
            return ['order-icon', active];
		},
		getAriaLabel(iconName) {
			if (this.getActiveStatus(iconName)) {
				return `current order status: ${iconName}`;
			}
		},
        getActiveStatus(ICON_NAME) {
			if (!this.order) {
				return;
			}
            // 55 is greater than all statuses, would highlight everything
            if (this.order.serviceType === EServiceType.SERVICE_TYPE_DELIVERY) {
                if (ICON_NAME === ICON_NAMES.PIZZA_TRACKER.ON_ITS_WAY) {
                    // on it's way should be highlighted if delivered or on route. handled here
                    if (this.order.status === EOrderStatus.STATUS_DELIVERED || this.order.status === EOrderStatus.STATUS_ON_ROUTE) {
                        return true;
                    }
                } else if (ICON_NAME === ICON_NAMES.PIZZA_TRACKER.EATING) {
                    return this.order.status === EOrderStatus.STATUS_DELIVERED;
                } else { 
                    return this.iconMap[ICON_NAME].status <= this.order.status;
                }
            } else {
                // pickup treated normally
                return this.iconMap[ICON_NAME].status <= this.order.status;
            }
        },
        sendNotificationOnReady(status, serviceType) {
            if ( (serviceType === EServiceType.SERVICE_TYPE_PICKUP || serviceType === EServiceType.SERVICE_TYPE_TAKEOUT) &&
                  status === EOrderStatus.STATUS_STAGED) {
                this.triggerNotification("Great news!", SUCCESS_MSG.ORDER.STATUS.PICKUP_READY, storeIcon)
            }
             else if ( (serviceType === EServiceType.SERVICE_TYPE_DELIVERY) && status === EOrderStatus.STATUS_ON_ROUTE ) [
                this.triggerNotification("Great news!", SUCCESS_MSG.ORDER.STATUS.ON_ITS_WAY, storeIcon)
            ]
		},
		orderTrackerStyle() {
			return {
				'is-marginless': this.isStreamLined,
			};
		},
		orderPromiseTimeStyle() {
			return {
				'flex-order-2': this.isStreamLined,
			};
		},
        generateOrderTrackerWebSocket() {
            try {
                if (this.order) {
                    if (this.order.status < EOrderStatus.STATUS_DELIVERED) {
                        if (this.orderLocation && this.orderLocation.pushSecure) {
                            const wsUrl = `wss://${this.orderLocation.pushHost}:${this.orderLocation.pushPort}/wspush/ois-${
                                this.order.id
							}`;
                            const websocket = new WebSocket(wsUrl);

                            const ping = evt => {
                                const start = evt.data.indexOf(":");
                                const end = evt.data.indexOf("{");
                                const ack = "ACK:" + evt.data.substring(start + 1, end - 1);
                                doSend(ack);

                                const res = JSON.parse(evt.data.substring(end));
                                if (res.notify === "notify_push_order_items_status") {
                                    const lineItemStatus = parseFloat(res.object.line_item_status);
                                    // never go down
                                    if (this.order.status < lineItemStatus) {
                                        this.order.setOrderStatus(lineItemStatus);
									}
									this.setTrackerPosition(lineItemStatus);
                                    this.sendNotificationOnReady(lineItemStatus, this.order.serviceType);

                                    if (lineItemStatus === EOrderStatus.STATUS_DELIVERED) {
                                        // remove active order as it has been delivered
                                        if (this.activeOrder && this.activeOrder.id === this.order.id) {
                                            this.setActiveOrder(null);
                                        }
                                        websocket.close();
                                    }
                                }
                            };

                            const onOpen = evt => {
                                try {
                                    doSend(`ois-${this.order.id}`);
                                } catch (err) {
                                    console.log(err);
                                }
                            };

                            const onErr = evt => {
                                console.log(evt);
                            };

                            const onMsg = evt => {
                                if (evt.data.indexOf("JSON:") === 0) {
                                    ping(evt);
                                } else {
                                    console.log("connection will close");
                                }
                            };

                            const onClose = evt => {
                                console.log(evt);
                            };

                            websocket.onopen = function(evt) {
                                onOpen(evt);
                            };
                            websocket.onclose = function(evt) {
                                onClose(evt);
                            };
                            websocket.onmessage = function(evt) {
                                onMsg(evt);
                            };
                            websocket.onerror = function(evt) {
                                onErr(evt);
                            };

                            const doSend = message => {
                                websocket.send(message);
                            };
                        }
                    }
                }
            } catch (err) {
                console.log(err);
            }
        }
	},
    mounted() {
		this.generateOrderTrackerWebSocket();
    }
};
</script>

<style lang="scss">
@import "globalStyles";

#order-tracker {
    width: 100%;
    border-radius: 5px;
    border: 1px solid $greyOutline;
    padding: 1em;
    margin: 2em 0em;
    display: flex;
    flex-direction: column;
    padding-top: 10px;
}

.order-status-icon {
    color: $greyOutline;
}

.order-hr {
    margin: 0 auto;
    width: 6em;
    background-color: $greyOutline;
}
.order-hr.active {
    background-color: $navigationItemColor;
    height: 2px;
}

@include mobile {
    .order-hr {
        width: 100%;
        max-width: 3em;
    }

    #order-tracker {
        border: none;
        font-size: 0.8rem;
        padding: 0em;
		margin: 0em;
		font-family: $secondaryFont;

        p {
            position: relative;
            top: -12px;
        }
        .order-promise-time {
            position: relative;
            top: -12px;
        }
        .columns {
            line-height: 1.9rem;
        }
    }
}
</style>


// WEBPACK FOOTER //
// src/components/orders/OrderTracker.vue